// frontend/src/components/engineer/pages/EngineerLeaveReportsPage.tsx
// Engineer Leave Reports - Professional Excel-Style Table Format
// PHOENICIAN TECHNICAL SERVICES LLC

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AuthUser } from '../../../services/auth.service';
import { 
  engineerLeaveService, 
  LeaveApplicationRecord,
  LEAVE_TYPES,
  LeaveType
} from '../../../services/engineerLeave.service';
import styles from './EngineerLeaveReportsPage.module.css';

interface EngineerLeaveReportsPageProps {
  user?: AuthUser;
  onNavigate?: (page: string) => void;
}

type TabType = 'all' | 'pending' | 'approved' | 'rejected';
type SortField = 'applicationDate' | 'employeeName' | 'leaveType' | 'totalDays' | 'status';
type SortDirection = 'asc' | 'desc';

const EngineerLeaveReportsPage: React.FC<EngineerLeaveReportsPageProps> = ({ user, onNavigate }) => {
  // State
  const [applications, setApplications] = useState<LeaveApplicationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('applicationDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedApplication, setSelectedApplication] = useState<LeaveApplicationRecord | null>(null);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    byType: { annual: 0, sick: 0, local: 0, emergency: 0 } as Record<LeaveType, number>,
    totalDays: 0,
    thisMonth: 0
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  // Fetch data
  const fetchData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const engineerId = user.employeeNo || user.uid;
      const [leaves, leaveStats] = await Promise.all([
        engineerLeaveService.getEngineerAppliedLeaves(engineerId),
        engineerLeaveService.getEngineerLeaveStats(engineerId)
      ]);
      
      setApplications(leaves);
      setStats(leaveStats);
    } catch (err: any) {
      console.error('Error fetching leave reports:', err);
      setError('Failed to load leave reports. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Filter & Sort
  const filteredApplications = useMemo(() => {
    let filtered = [...applications];
    
    if (activeTab !== 'all') {
      filtered = filtered.filter(app => app.hrApprovalStatus === activeTab);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app =>
        app.employeeName.toLowerCase().includes(query) ||
        app.employeePin.toLowerCase().includes(query) ||
        app.leaveType.toLowerCase().includes(query)
      );
    }
    
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'applicationDate':
          comparison = a.applicationDate.getTime() - b.applicationDate.getTime();
          break;
        case 'employeeName':
          comparison = a.employeeName.localeCompare(b.employeeName);
          break;
        case 'leaveType':
          comparison = a.leaveType.localeCompare(b.leaveType);
          break;
        case 'totalDays':
          comparison = a.totalDays - b.totalDays;
          break;
        case 'status':
          comparison = a.hrApprovalStatus.localeCompare(b.hrApprovalStatus);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [applications, activeTab, searchQuery, sortField, sortDirection]);
  
  // Pagination
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredApplications.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredApplications, currentPage]);
  
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  
  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  const formatDateTime = (date: Date): string => {
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending': return 'Pending';
      default: return 'Pending';
    }
  };
  
  const getLeaveTypeInfo = (type: LeaveType) => {
    return LEAVE_TYPES.find(lt => lt.id === type) || LEAVE_TYPES[0];
  };
  
  // Render loading
  if (isLoading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading leave reports...</p>
        </div>
      </div>
    );
  }
  
  // Render error
  if (error) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <h3>Error Loading Reports</h3>
          <p>{error}</p>
          <button onClick={fetchData} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <button onClick={() => onNavigate?.('apply-leave')} className={styles.backButton}>
            Back to Apply Leave
          </button>
        </div>
        <div className={styles.headerCenter}>
          <h1>Leave Reports</h1>
          <p>All leave applications submitted via Engineer Portal</p>
        </div>
        <div className={styles.headerRight}>
          <button onClick={fetchData} className={styles.refreshButton}>
            Refresh
          </button>
        </div>
      </div>
      
      {/* Summary Stats - Compact Row */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.total}</span>
          <span className={styles.statLabel}>Total</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={`${styles.statValue} ${styles.pending}`}>{stats.pending}</span>
          <span className={styles.statLabel}>Pending</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={`${styles.statValue} ${styles.approved}`}>{stats.approved}</span>
          <span className={styles.statLabel}>Approved</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={`${styles.statValue} ${styles.rejected}`}>{stats.rejected}</span>
          <span className={styles.statLabel}>Rejected</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.totalDays}</span>
          <span className={styles.statLabel}>Total Days (Approved)</span>
        </div>
        <div className={styles.statDivider}></div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{stats.thisMonth}</span>
          <span className={styles.statLabel}>This Month</span>
        </div>
      </div>
      
      {/* Leave Type Summary - Compact */}
      <div className={styles.typeSummaryBar}>
        {LEAVE_TYPES.map(type => (
          <div key={type.id} className={styles.typeItem}>
            <span className={styles.typeName}>{type.displayName}:</span>
            <span className={styles.typeCount}>{stats.byType[type.id] || 0}</span>
          </div>
        ))}
      </div>
      
      {/* Controls Bar */}
      <div className={styles.controlsBar}>
        <div className={styles.tabsContainer}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'all' ? styles.activeTab : ''}`}
            onClick={() => { setActiveTab('all'); setCurrentPage(1); }}
          >
            All ({applications.length})
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'pending' ? styles.activeTab : ''}`}
            onClick={() => { setActiveTab('pending'); setCurrentPage(1); }}
          >
            Pending ({stats.pending})
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'approved' ? styles.activeTab : ''}`}
            onClick={() => { setActiveTab('approved'); setCurrentPage(1); }}
          >
            Approved ({stats.approved})
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'rejected' ? styles.activeTab : ''}`}
            onClick={() => { setActiveTab('rejected'); setCurrentPage(1); }}
          >
            Rejected ({stats.rejected})
          </button>
        </div>
        
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search by name, PIN, or leave type..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className={styles.clearButton}>Clear</button>
          )}
        </div>
      </div>
      
      {/* Data Table - Excel Style */}
      <div className={styles.tableContainer}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th className={styles.colSno}>S.No</th>
              <th 
                onClick={() => handleSort('applicationDate')} 
                className={`${styles.sortable} ${styles.colDate}`}
              >
                Date Applied {sortField === 'applicationDate' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
              </th>
              <th 
                onClick={() => handleSort('employeeName')} 
                className={`${styles.sortable} ${styles.colEmployee}`}
              >
                Employee Name {sortField === 'employeeName' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
              </th>
              <th className={styles.colPin}>PIN</th>
              <th 
                onClick={() => handleSort('leaveType')} 
                className={`${styles.sortable} ${styles.colType}`}
              >
                Leave Type {sortField === 'leaveType' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
              </th>
              <th className={styles.colFromDate}>From Date</th>
              <th className={styles.colToDate}>To Date</th>
              <th 
                onClick={() => handleSort('totalDays')} 
                className={`${styles.sortable} ${styles.colDays}`}
              >
                Days {sortField === 'totalDays' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
              </th>
              <th className={styles.colDoc}>Doc</th>
              <th 
                onClick={() => handleSort('status')} 
                className={`${styles.sortable} ${styles.colStatus}`}
              >
                Status {sortField === 'status' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
              </th>
              <th className={styles.colRemarks}>HR Remarks</th>
              <th className={styles.colActions}>View</th>
            </tr>
          </thead>
          <tbody>
            {paginatedApplications.length === 0 ? (
              <tr>
                <td colSpan={12} className={styles.emptyRow}>
                  <div className={styles.emptyState}>
                    <h4>No Leave Applications Found</h4>
                    <p>
                      {searchQuery 
                        ? 'Try adjusting your search criteria'
                        : activeTab !== 'all'
                          ? `No ${activeTab} applications available`
                          : 'No leave applications have been submitted yet'
                      }
                    </p>
                    <button onClick={() => onNavigate?.('apply-leave')} className={styles.primaryButton}>
                      Apply New Leave
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedApplications.map((app, index) => {
                const leaveInfo = getLeaveTypeInfo(app.leaveType);
                const serialNo = ((currentPage - 1) * itemsPerPage) + index + 1;
                
                return (
                  <tr key={app.id}>
                    <td className={styles.centerCell}>{serialNo}</td>
                    <td>{formatDate(app.applicationDate)}</td>
                    <td className={styles.nameCell}>{app.employeeName}</td>
                    <td className={styles.centerCell}>{app.employeePin}</td>
                    <td>{leaveInfo.displayName}</td>
                    <td>{formatDate(app.startDate)}</td>
                    <td>{formatDate(app.endDate)}</td>
                    <td className={styles.centerCell}>{app.totalDays}</td>
                    <td className={styles.centerCell}>
                      {app.certificateUrl ? (
                        <a 
                          href={app.certificateUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.docLink}
                        >
                          View
                        </a>
                      ) : (
                        <span className={styles.noData}>-</span>
                      )}
                    </td>
                    <td className={styles.centerCell}>
                      <span className={`${styles.statusLabel} ${styles[`status${app.hrApprovalStatus.charAt(0).toUpperCase() + app.hrApprovalStatus.slice(1)}`]}`}>
                        {getStatusText(app.hrApprovalStatus)}
                      </span>
                    </td>
                    <td className={styles.remarksCell}>
                      {app.hrRemarks ? (
                        <span title={app.hrRemarks}>{app.hrRemarks}</span>
                      ) : (
                        <span className={styles.noData}>
                          {app.hrApprovalStatus === 'pending' ? 'Awaiting...' : '-'}
                        </span>
                      )}
                    </td>
                    <td className={styles.centerCell}>
                      <button 
                        onClick={() => setSelectedApplication(app)}
                        className={styles.viewButton}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredApplications.length)} of {filteredApplications.length} records
          </span>
          <div className={styles.paginationControls}>
            <button 
              onClick={() => setCurrentPage(1)} 
              disabled={currentPage === 1}
              className={styles.pageButton}
            >
              First
            </button>
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
              disabled={currentPage === 1}
              className={styles.pageButton}
            >
              Prev
            </button>
            <span className={styles.pageIndicator}>
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
              disabled={currentPage === totalPages}
              className={styles.pageButton}
            >
              Next
            </button>
            <button 
              onClick={() => setCurrentPage(totalPages)} 
              disabled={currentPage === totalPages}
              className={styles.pageButton}
            >
              Last
            </button>
          </div>
        </div>
      )}
      
      {/* Detail Modal */}
      {selectedApplication && (
        <div className={styles.modalOverlay} onClick={() => setSelectedApplication(null)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Leave Application Details</h2>
              <button onClick={() => setSelectedApplication(null)} className={styles.closeButton}>Close</button>
            </div>
            
            <div className={styles.modalBody}>
              {/* Status Banner */}
              <div className={`${styles.statusBanner} ${styles[`status${selectedApplication.hrApprovalStatus.charAt(0).toUpperCase() + selectedApplication.hrApprovalStatus.slice(1)}`]}`}>
                Status: {getStatusText(selectedApplication.hrApprovalStatus)}
              </div>
              
              {/* Employee Info */}
              <div className={styles.detailSection}>
                <h3>Employee Information</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Name</span>
                    <span className={styles.detailValue}>{selectedApplication.employeeName}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>PIN</span>
                    <span className={styles.detailValue}>{selectedApplication.employeePin}</span>
                  </div>
                  {selectedApplication.department && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Department</span>
                      <span className={styles.detailValue}>{selectedApplication.department}</span>
                    </div>
                  )}
                  {selectedApplication.designation && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Designation</span>
                      <span className={styles.detailValue}>{selectedApplication.designation}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Leave Details */}
              <div className={styles.detailSection}>
                <h3>Leave Details</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Leave Type</span>
                    <span className={styles.detailValue}>{getLeaveTypeInfo(selectedApplication.leaveType).displayName}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>From</span>
                    <span className={styles.detailValue}>{formatDate(selectedApplication.startDate)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>To</span>
                    <span className={styles.detailValue}>{formatDate(selectedApplication.endDate)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Duration</span>
                    <span className={styles.detailValue}>{selectedApplication.totalDays} day(s)</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Application Date</span>
                    <span className={styles.detailValue}>{formatDateTime(selectedApplication.applicationDate)}</span>
                  </div>
                </div>
                
                {selectedApplication.reason && (
                  <div className={styles.reasonBox}>
                    <span className={styles.detailLabel}>Reason</span>
                    <p>{selectedApplication.reason}</p>
                  </div>
                )}
              </div>
              
              {/* Certificate */}
              {selectedApplication.certificateUrl && (
                <div className={styles.detailSection}>
                  <h3>Supporting Document</h3>
                  <div className={styles.documentBox}>
                    <span>{selectedApplication.certificateFileName || 'Document attached'}</span>
                    <div className={styles.docActions}>
                      <a 
                        href={selectedApplication.certificateUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.docButton}
                      >
                        View
                      </a>
                      <a 
                        href={selectedApplication.certificateUrl} 
                        download={selectedApplication.certificateFileName}
                        className={styles.docButton}
                      >
                        Download
                      </a>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Engineer Remarks */}
              {selectedApplication.engineerRemarks && (
                <div className={styles.detailSection}>
                  <h3>Engineer Remarks</h3>
                  <div className={styles.remarksBox}>
                    <p>{selectedApplication.engineerRemarks}</p>
                    {selectedApplication.appliedByEngineerName && (
                      <span className={styles.remarksAuthor}>- {selectedApplication.appliedByEngineerName}</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* HR Decision */}
              {(selectedApplication.hrApprovalStatus === 'approved' || selectedApplication.hrApprovalStatus === 'rejected') && (
                <div className={styles.detailSection}>
                  <h3>HR Decision</h3>
                  <div className={`${styles.hrDecisionBox} ${styles[`status${selectedApplication.hrApprovalStatus.charAt(0).toUpperCase() + selectedApplication.hrApprovalStatus.slice(1)}`]}`}>
                    <div className={styles.hrDecisionHeader}>
                      <span className={styles.hrDecisionStatus}>
                        {selectedApplication.hrApprovalStatus === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                      {selectedApplication.hrApprovedAt && (
                        <span className={styles.hrDecisionDate}>
                          {formatDateTime(selectedApplication.hrApprovedAt)}
                        </span>
                      )}
                    </div>
                    {selectedApplication.hrRemarks && (
                      <p className={styles.hrRemarks}>{selectedApplication.hrRemarks}</p>
                    )}
                    {selectedApplication.hrManagerName && (
                      <span className={styles.hrManager}>HR Manager: {selectedApplication.hrManagerName}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className={styles.modalFooter}>
              <button onClick={() => setSelectedApplication(null)} className={styles.closeModalButton}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineerLeaveReportsPage;